/**
 * Error Reporting Service
 *
 * Lightweight error tracking. In production, this sends errors
 * to the backend for logging. Replace with Sentry when ready.
 */

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  timestamp: string;
  userAgent: string;
  userId?: string;
}

class ErrorReportingService {
  private readonly endpoint: string;
  private readonly enabled: boolean;

  constructor() {
    this.endpoint = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'}/monitoring/client-errors`;
    this.enabled = import.meta.env.PROD;
  }

  report(error: Error, extra?: { componentStack?: string }): void {
    if (!this.enabled) return;

    const report: ErrorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: extra?.componentStack,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    // Fire-and-forget — don't block the UI
    const payload = JSON.stringify(report);
    const sent = navigator.sendBeacon?.(this.endpoint, payload);
    if (!sent) {
      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {
        /* silent */
      });
    }
  }

  captureException(error: unknown): void {
    if (error instanceof Error) {
      this.report(error);
    } else {
      this.report(new Error(String(error)));
    }
  }
}

export const errorReporting = new ErrorReportingService();
